import { useState, useEffect, useCallback, useRef, useMemo } from 'react'
import dayjs from 'dayjs'
import specializedReportService, { specializedAttachFormats } from '@api/v1/specializedReportService'
import reportJobService from '@api/v1/reportJobService'
import useLatestRun from '@hooks/useLatestRun'
import { toTkaWireFilter } from './timekeeperAnalysisFilter'
import SpecializedReportShell from '@components/reports/SpecializedReportShell'
import { timekeeperAnalysisSavedCriteria } from '@components/reports/savedRunCriteria'
import SpecializedFilterBar from '@components/reports/SpecializedFilterBar'
import { objectField, dateRangeField, choiceField } from '@components/reports/specializedFilterFields'
import ReportViewsMenu from '@components/reports/ReportViewsMenu'
import useSavedReportView from '@components/reports/useSavedReportView'
import { describeFilters, dateRangePart } from '@components/reports/reportFilterSummary'
import CsvReportGrid from '@components/reports/CsvReportGrid'
import { exportFileName } from '@components/reports/reportGridUtils'

const TYPE_USERS = 1
const TYPE_COMPANIES = 3

// CC-49834: legacy TKA exposes an "Invoice Posted" filter —
// TimekeeperAnalysisFilter.postedFilter is a nullable bool (null = both).
// Same tri-state model (and wording) as the AR page's posted select.
const POSTED_OPTIONS = [
  { value: '', label: "Don't filter" },
  { value: 'true', label: 'Posted' },
  { value: 'false', label: 'Unposted' },
]

// The field specs address this page's OWN state keys; `toTkaWireFilter()` stays
// the single wire boundary and is what renames them (clientId → companyId,
// entryDateStart → dateStart, userId → userIds, plus `print`). Keeping that
// translation in one place is why the migration cannot change the wire.
//
// `postedFilter` is a genuine THREE-state (bool?): null means "don't filter",
// which is not the same as false. encode/decode must not collapse them.
const FIELDS = [
  objectField({ key: 'client', label: 'Client', typeId: TYPE_COMPANIES, idKey: 'clientId', nameKey: 'clientName' }),
  objectField({ key: 'timekeeper', label: 'Timekeeper', typeId: TYPE_USERS, idKey: 'userId', nameKey: 'userName' }),
  dateRangeField({ key: 'entryDates', label: 'Date Range', startKey: 'entryDateStart', endKey: 'entryDateEnd', omitAll: true }),
  choiceField({
    key: 'posted', label: 'Invoice Posted', valueKey: 'postedFilter',
    options: POSTED_OPTIONS.map((o) => ({ value: o.value, label: o.label })),
    encode: (v) => (v == null ? '' : String(v)),
    decode: (s) => (s === '' ? null : s === 'true'),
  }),
]

const makeDefaults = () => ({
  clientId: null, clientName: '',
  userId: null, userName: '',
  entryDateStart: dayjs().startOf('year').format('YYYY-MM-DD'),
  entryDateEnd: dayjs().format('YYYY-MM-DD'),
  postedFilter: null,
})

// Module-level so useLatestRun's `run` keeps one identity (spec 131).
const runErrorMessage = (err) => err?.response?.data?.error || err.message || 'Report failed'

const TimekeeperAnalysisReport = ({ reportId, name = 'Timekeeper Analysis', summary, jobId }) => {
  // CC-50026: no Matter filter here. CB's TimekeeperAnalysisFilter
  // (Endpoint/MesaUtilitiesBase/AccountingModels.cs:1064) has no matter /
  // billing-object property, so a matter selection can never reach the proc —
  // V1's page offers only Client + Professional. The lookup used to render
  // anyway and silently did nothing; QA's own alternative was to remove it.
  // CC-49834: postedFilter rides the filters JSON straight into the legacy CSV
  // export (Newtonsoft binds it onto TimekeeperAnalysisFilter.postedFilter).
  // Tenant-specific custom field IDs are resolved server-side from CB server
  // settings; cb-api injects them into the filter before calling the legacy
  // CSV-export endpoint. See REPORT_TENANT_SETTINGS in
  // cb-api/src/domains/reports/routes.js.
  const [filters, setFilters] = useState(makeDefaults)
  // Spec 131: only the NEWEST run may write the rows, the spinner, the error or
  // the documents' criteria (the split button's mid-run "Run report now" is also
  // disabled now; this gate is the structural guarantee).
  const {
    running, error, ran, runKey, run: startRun, invalidate: invalidateRun,
  } = useLatestRun({ initialRunning: !!jobId, errorMessage: runErrorMessage })
  // CC-50013 (G3): saved views. The hook carries the grid's page state, so
  // the menu cannot be mounted without it (the CC-49732 defect).
  const view = useSavedReportView(setFilters)
  // CC-50029: exports state the period they cover.
  const filtersSummary = useMemo(() => describeFilters(dateRangePart(filters.entryDateStart, filters.entryDateEnd)), [filters])
  const [data, setData] = useState({ headers: [], rows: [], meta: [] })
  const [savedAsOf, setSavedAsOf] = useState(null)
  // Spec 185 (CC-53435): the reopened job's criteria line, built from ITS stored params
  // at the reopen's commit (null = the shell's "not recorded"). Never seeded into
  // `filters`: the filter bar is not the saved run's, and a reopen must not start a run.
  const [savedCriteria, setSavedCriteria] = useState(null)
  // Consume-once: the first run reopens the tray's saved job; later runs are fresh.
  const jobRef = useRef(jobId || null)

  const runReport = useCallback(() => {
    if (!filters.entryDateStart || !filters.entryDateEnd) {
      // Spec 131: the refusal also retires a run still in flight.
      invalidateRun({ error: 'Date range is required.' })
      return undefined
    }
    return startRun(async () => {
      const jid = jobRef.current
      jobRef.current = null // consumed — subsequent runs are fresh
      if (jid) {
        // Tray "View report" → reopen the exact saved snapshot + show its run time.
        const [st, r] = await Promise.all([reportJobService.status(jid), reportJobService.result(jid)])
        return () => { setSavedAsOf(st?.createdAt || null); setSavedCriteria(timekeeperAnalysisSavedCriteria(st?.savedParams)); setData(r?.data || { headers: [], rows: [], meta: [] }) }
      }
      const result = await specializedReportService.timekeeperAnalysis.run(toTkaWireFilter(filters), { reportId })
      // Spec 131 decision 3: the saved-result banner clears at the COMMIT.
      return () => { setSavedAsOf(null); setData(result || { headers: [], rows: [], meta: [] }) }
    }, { criteria: { filtersSummary } })
  }, [filters, reportId, startRun, invalidateRun, filtersSummary])

  // Spec 131: deferred and cancellable, so StrictMode's simulated remount starts
  // ONE mount run, not two.
  useEffect(() => {
    const t = setTimeout(() => { runReport() }, 0)
    return () => clearTimeout(t)
  }, [])  // eslint-disable-line react-hooks/exhaustive-deps

  // CC-50013 G2 / CC-50148: shared metadata-driven bar (see FIELDS).
  const filterBar = (
    <SpecializedFilterBar
      fields={FIELDS}
      filters={filters}
      onFiltersChange={setFilters}
      defaults={makeDefaults()}
      onRun={runReport}
      isLoading={running}
      queue={{
        reportId,
        name,
        actionKey: 'timekeeper-analysis',
        // Queues the TRANSLATED wire, exactly as the interactive run does.
        endpoint: (f) => ({ method: 'POST', path: '/reports/timekeeper-analysis', body: toTkaWireFilter(f) }),
        // Spec 200: an emailed copy carries this report's own export, asked what the run asks.
        attachFormats: specializedAttachFormats('/reports/timekeeper-analysis'),
      }}
      actions={(
        <ReportViewsMenu
          storageKey={'report:timekeeper-analysis'}
          currentFilters={filters}
          onApply={view.applyView}
          getPageState={view.getPageState}
        />
      )}
    />
  )

  return (
    <SpecializedReportShell
      name={name} summary={summary} running={running} error={error}
      ranCriteria={ran?.criteria ?? null} runKey={runKey}
      filterBar={filterBar} filtersSummary={filtersSummary} summaryData={data} savedAsOf={savedAsOf}
      savedRunFiltersSummary={savedAsOf != null ? savedCriteria : undefined}
      download={(format, getToken) =>
        // CC-49852: V1's "{ts}_{Report_Name}.{ext}" filename convention.
        specializedReportService.timekeeperAnalysis.download(toTkaWireFilter(filters), format, getToken, exportFileName(name, format))
      }
    >
      <CsvReportGrid key={view.gridKey} data={data} storageKey="report:timekeeper-analysis" pageStateRef={view.pageStateRef} initialPageState={view.initialPageState} />
    </SpecializedReportShell>
  )
}

export default TimekeeperAnalysisReport
